import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Send, Rocket, Wifi, WifiOff, AlertTriangle } from 'lucide-react';
import { Card, SectionTitle, btnPrimary, btnGhost } from './MlmShell';

const Toggle = ({ checked, onChange, testId, disabled }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} data-testid={testId}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition ${checked ? 'bg-[#0B3F8C]' : 'bg-slate-300'} disabled:opacity-50`}>
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const NET_META = [
  { id: 'instagram', label: 'Instagram', icon: '📸' },
  { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { id: 'telegram', label: 'Telegram', icon: '✈️' },
  { id: 'gmail', label: 'Gmail', icon: '📧' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  { id: 'email', label: 'Email', icon: '📨' },
];

function MisRedesCard({ api, setSection }) {
  const [connStatus, setConnStatus] = useState(null);

  useEffect(() => {
    api('/connections/status', {}, { silent: true, key: 'mis-redes' })
      .then(d => setConnStatus(d))
      .catch(() => {});
  }, [api]);

  const getStatus = (net) => {
    if (!connStatus) return 'loading';
    const s = connStatus[net.id];
    if (net.id === 'email') return 'connected';
    return (s?.status) || 'disconnected';
  };

  const connectedCount = NET_META.filter(n => getStatus(n) === 'connected').length;

  return (
    <Card testId="mlm-mis-redes-card">
      <div className="flex items-center justify-between">
        <SectionTitle kicker="Mis Redes" title="Estado de conexiones"
          subtitle={connStatus ? `${connectedCount} de ${NET_META.length} redes activas` : 'Comprobando…'} />
        <button type="button" onClick={() => setSection('connections')}
          className={`${btnGhost} shrink-0`} data-testid="mlm-redes-go-connections">
          Gestionar
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="mlm-redes-grid">
        {NET_META.map(net => {
          const st = getStatus(net);
          const isConn = st === 'connected';
          const isErr = st === 'error';
          return (
            <div key={net.id}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition
                ${isConn ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                  isErr ? 'border-red-200 bg-red-50 text-red-700' :
                  st === 'loading' ? 'border-slate-100 bg-slate-50 text-slate-400 animate-pulse' :
                  'border-slate-100 bg-slate-50 text-slate-400'}`}
              data-testid={`mlm-redes-badge-${net.id}`}>
              <span>{net.icon}</span>
              <span className="truncate">{net.label}</span>
              {isConn ? <Wifi className="ml-auto h-3.5 w-3.5 text-emerald-500 shrink-0" />
                : isErr ? <AlertTriangle className="ml-auto h-3.5 w-3.5 text-red-400 shrink-0" />
                : <WifiOff className="ml-auto h-3.5 w-3.5 text-slate-300 shrink-0" />}
            </div>
          );
        })}
      </div>
      {connStatus && connectedCount < 2 && (
        <p className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          Conecta al menos WhatsApp e Instagram para aprovechar la automatización completa de Aynara.
        </p>
      )}
    </Card>
  );
}

export const MlmOverview = ({ workspace, api, busy, setSection }) => {
  const { follow_ups_today: followUps, metrics, tasks, content, aynara_log: log, stage_counts: stages, options, settings = {} } = workspace;
  const pending = tasks.filter((t) => t.status !== 'completado').slice(0, 3);
  const drafts = content.filter((c) => c.status === 'borrador').slice(0, 3);
  const scheduledToday = content.filter((c) => (c.scheduled_for || '').slice(0, 10) === new Date().toISOString().slice(0, 10) && ['programado', 'aprobado'].includes(c.status));
  const setSetting = (payload) => api('/settings', { method: 'PUT', body: JSON.stringify(payload) }, { key: 'mlm-settings', silent: false });
  const sendDigestNow = () => api('/aynara/daily-digest', { method: 'POST' }, { success: 'Resumen del día enviado a Laura', key: 'mlm-digest-now' });
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* Mis Redes — compact status at a glance */}
      <div className="lg:col-span-3">
        <MisRedesCard api={api} setSection={setSection} />
      </div>
      <Card className="lg:col-span-3" testId="mlm-overview-automation">
        <SectionTitle kicker="Automatización" title="Aynara trabaja en automático" subtitle="Recordatorios cada mañana y publicación de tu calendario a su hora, sin que tengas que pulsar nada." />
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-100 bg-[#fbfcfe] p-4" data-testid="mlm-automation-digest">
            <div>
              <p className="flex items-center gap-2 font-black text-[#062A5E]"><Bell className="h-4 w-4 text-[#E2001A]" />Recordatorio diario</p>
              <p className="mt-1 text-sm text-slate-600">Cada mañana Aynara te envía por email tus seguimientos del día y los posts programados para hoy ({scheduledToday.length} hoy).</p>
              <p className="mt-1 text-xs text-slate-400">{settings.daily_digest_last_sent ? `Último envío: ${settings.daily_digest_last_sent}` : 'Aún no se ha enviado hoy'}</p>
              <button type="button" onClick={sendDigestNow} disabled={busy === 'mlm-digest-now'} className={`${btnGhost} mt-3`} data-testid="mlm-digest-send-now"><Send className="h-3.5 w-3.5" />{busy === 'mlm-digest-now' ? 'Enviando…' : 'Enviar resumen ahora'}</button>
            </div>
            <Toggle checked={!!settings.daily_digest_enabled} onChange={(v) => setSetting({ daily_digest_enabled: v })} testId="mlm-toggle-digest" disabled={busy === 'mlm-settings'} />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-100 bg-[#fbfcfe] p-4" data-testid="mlm-automation-autopublish">
            <div>
              <p className="flex items-center gap-2 font-black text-[#062A5E]"><Rocket className="h-4 w-4 text-[#0B3F8C]" />Publicación programada</p>
              <p className="mt-1 text-sm text-slate-600">Cada post del calendario se publica solo, a su hora, en la red conectada (Instagram, Facebook o TikTok).</p>
              <p className="mt-1 text-xs text-slate-400">Requiere tener la red conectada en «Conexiones». TikTok necesita un vídeo asignado.</p>
              <button type="button" onClick={() => setSection('connections')} className={`${btnGhost} mt-3`} data-testid="mlm-autopublish-connect">Ir a Conexiones</button>
            </div>
            <Toggle checked={!!settings.auto_publish_enabled} onChange={(v) => setSetting({ auto_publish_enabled: v })} testId="mlm-toggle-autopublish" disabled={busy === 'mlm-settings'} />
          </div>
        </div>
      </Card>

      <Card className="lg:col-span-2" testId="mlm-overview-followups">
        <SectionTitle kicker="Hoy" title={`${followUps.length} seguimientos para hoy`} subtitle="Aynara los ordena por prioridad. Un clic y tienes el mensaje de WhatsApp listo." action={<button type="button" onClick={() => setSection('prospects')} className={btnGhost} data-testid="mlm-overview-open-prospects">Abrir CRM</button>} />
        <div className="mt-5 space-y-2">
          {followUps.length === 0 && <p className="rounded-2xl bg-[#f1f5fb] px-4 py-5 text-sm text-slate-500" data-testid="mlm-overview-no-followups">Sin seguimientos pendientes. Añade prospectos o pídele a Aynara: “programa seguimiento con Ana en 2 días”.</p>}
          {followUps.slice(0, 6).map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-2xl border border-slate-100 bg-[#fbfcfe] px-4 py-3" data-testid={`mlm-followup-${p.id}`}>
              <div>
                <p className="font-bold text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">{p.next_action || 'Seguimiento'} · {p.source} · {options.stages.find((s) => s.id === p.stage)?.label}</p>
              </div>
              <span className="rounded-full bg-[#E2001A]/10 px-3 py-1 text-xs font-bold text-[#E2001A]">Score {p.score}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card testId="mlm-overview-activation">
        <SectionTitle kicker="Activación 369" title={`${metrics.activation_progress}% completado`} />
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-[#0B3F8C] transition-all" style={{ width: `${metrics.activation_progress}%` }} /></div>
        <ul className="mt-4 space-y-2 text-sm">
          {pending.map((t) => <li key={t.id} className="rounded-xl bg-[#f1f5fb] px-3 py-2 text-slate-700">Día {t.day} · {t.title}</li>)}
        </ul>
        <button type="button" onClick={() => setSection('activation')} className={`${btnPrimary} mt-4 w-full`} data-testid="mlm-overview-open-activation">Ver plan completo</button>
      </Card>

      <Card testId="mlm-overview-pipeline">
        <SectionTitle kicker="Embudo" title="Tu pipeline" />
        <div className="mt-4 space-y-2">
          {options.stages.map((s) => (
            <div key={s.id} className="flex items-center gap-3 text-sm">
              <span className="w-40 truncate text-slate-600">{s.label}</span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[#0B3F8C]" style={{ width: `${Math.min(100, (stages[s.id] || 0) * 12)}%` }} /></div>
              <span className="w-6 text-right font-bold text-[#062A5E]">{stages[s.id] || 0}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card testId="mlm-overview-content">
        <SectionTitle kicker="Redes" title="Borradores listos" subtitle="Revisa, aprueba y publica." />
        <div className="mt-4 space-y-2">
          {drafts.length === 0 && <p className="text-sm text-slate-500">Aún no hay borradores. Genera tu primer post en Redes.</p>}
          {drafts.map((c) => <div key={c.id} className="rounded-xl bg-[#f1f5fb] px-3 py-2 text-sm"><p className="font-bold text-[#062A5E]">{c.channel} · {c.goal_label}</p><p className="line-clamp-2 text-slate-600">{c.body}</p></div>)}
        </div>
        <button type="button" onClick={() => setSection('content')} className={`${btnGhost} mt-4`} data-testid="mlm-overview-open-content">Ir a Redes</button>
      </Card>

      <Card testId="mlm-overview-aynara">
        <SectionTitle kicker="Aynara" title="Últimas órdenes" />
        <div className="mt-4 space-y-2">
          {log.length === 0 && <p className="text-sm text-slate-500">Prueba: “Aynara, dame el resumen del día”.</p>}
          {log.slice(0, 4).map((l) => <div key={l.id} className="rounded-xl border border-slate-100 px-3 py-2 text-sm"><p className="text-slate-500">“{l.text}”</p><p className="font-semibold text-[#0B3F8C]">{l.reply}</p></div>)}
        </div>
        <button type="button" onClick={() => setSection('aynara')} className={`${btnPrimary} mt-4 w-full`} data-testid="mlm-overview-open-aynara">Hablar con Aynara</button>
      </Card>
    </div>
  );
};
